// Stored-file media that survives an expired signature (E-085).
//
// Every list endpoint signs its file URLs once and the SPA keeps that response
// in memory for as long as the tab stays open, so a link signed at 9am is dead
// by mid-afternoon — the user gets Azure's raw `AuthenticationFailed` XML page.
// The backend fix made the lifetimes honest; these two components are what
// makes a *stale* URL recover instead of breaking.
//
// Drop-in for `<img>` and `<a>`:
//   <BlobImg  src={m.attachmentUrl} alt="…" />
//   <BlobLink href={m.attachmentUrl} target="_blank">…</BlobLink>
//
// Non-blob URLs (data:, YouTube, an external link) pass straight through with
// no extra work and no round trip.
import { useState, useEffect } from 'react';
import { freshBlobUrl, isDeskteBlobUrl } from '../api.js';

// An <img> that re-signs and retries ONCE when the browser fails to load it.
// Retry-on-error rather than sign-on-mount: the overwhelmingly common case is a
// URL that still works, and that case should cost nothing.
export function BlobImg({ src, onError, ...rest }) {
  const [url, setUrl] = useState(src);
  const [retried, setRetried] = useState(false);

  // A new src (different message, re-rendered list) starts over.
  useEffect(() => { setUrl(src); setRetried(false); }, [src]);

  const handleError = async (e) => {
    if (retried || !isDeskteBlobUrl(src)) { onError?.(e); return; }
    setRetried(true);
    const fresh = await freshBlobUrl(src);
    // Same URL back means signing failed — report the error rather than
    // silently reloading the identical broken image.
    if (fresh && fresh !== url) setUrl(fresh);
    else onError?.(e);
  };

  return <img {...rest} src={url} onError={handleError} />;
}

// Open a stored file in a new tab, re-signed first. For click handlers that
// aren't links (an image you click to see full size). Same gesture dance as
// BlobLink: claim the tab now, navigate it once the signature comes back.
export async function openBlobInNewTab(url) {
  if (!isDeskteBlobUrl(url)) { window.open(url, '_blank', 'noopener'); return; }
  const tab = window.open('about:blank', '_blank');
  if (tab) { try { tab.opener = null; } catch { /* cross-origin, already safe */ } }
  const fresh = await freshBlobUrl(url);
  if (tab) tab.location = fresh;
  else window.open(fresh, '_blank', 'noopener');
}

// An <a> that re-signs at click time. There's no "it failed" signal for a
// navigation — by the time Azure answers, the user is already looking at the
// XML — so unlike BlobImg this one always refreshes before opening.
export function BlobLink({ href, target, children, onClick, ...rest }) {
  const handleClick = async (e) => {
    onClick?.(e);
    if (e.defaultPrevented || !isDeskteBlobUrl(href)) return;
    e.preventDefault();

    // Claim the tab while we still hold the user gesture — one opened after
    // the `await` below is a popup, and the browser blocks it. Not 'noopener':
    // with it window.open returns null, stranding a blank tab. Cut `opener`
    // by hand instead, which gives the same protection.
    const newTab = target === '_blank';
    const tab = newTab ? window.open('about:blank', '_blank') : null;
    if (tab) { try { tab.opener = null; } catch { /* cross-origin, already safe */ } }

    const fresh = await freshBlobUrl(href);
    if (tab) tab.location = fresh;
    else if (newTab) window.open(fresh, '_blank', 'noopener');
    else window.location.assign(fresh);
  };

  return <a {...rest} href={href} target={target} onClick={handleClick}>{children}</a>;
}

// Un <audio> que se vuelve a firmar y reintenta UNA vez cuando el navegador no
// consigue cargarlo — el mismo trato que `BlobImg`, y por el mismo motivo.
//
// Hacía falta al abrir los adjuntos de TAREAS a audio (Asniel López, Pomares
// Insurance Group, 2026-09-11). Un audio con la firma vencida no da error a la
// vista: el reproductor sale puesto y al darle al play no suena nada, que es
// peor que una imagen rota — una imagen rota al menos se ve rota.
export function BlobAudio({ src, onError, ...rest }) {
  const [url, setUrl] = useState(src);
  const [retried, setRetried] = useState(false);

  useEffect(() => { setUrl(src); setRetried(false); }, [src]);

  const handleError = async (e) => {
    if (retried || !isDeskteBlobUrl(src)) { onError?.(e); return; }
    setRetried(true);
    const fresh = await freshBlobUrl(src);
    if (fresh && fresh !== url) setUrl(fresh);
    else onError?.(e);
  };

  return <audio {...rest} src={url} onError={handleError} />;
}
